import {useMutation, UseMutationOptions} from '@tanstack/react-query';
import {
  useCreateDocumentForExistingClientMutationFn,
  useCreateDocumentForNewClientMutationFn,
  useCreateDocumentForRecentClientMutationFn,
  UseCreateDocumentMutationReturnType,
} from '@/hooks/queries/documents';
import {ContractHolder, LatestTransaction} from '@/repositories/beneva-api/endpoints/documents';
import {analyticsHandler} from '@/services/analytics/analytics-handler';
import {DetailedInvestorAccount, Investor} from '@/types/client-info';

export type NewDocumentCreationDialogAction =
  | {type: 'new'; advisorCode: string}
  | {type: 'recent'; latestTransaction: LatestTransaction; advisorCode: string; contractHolder: ContractHolder}
  | {type: 'newAccount'; advisorCode: string; investor: Investor}
  | {type: 'existingAccount'; advisorCode: string; investor: Investor; account: DetailedInvestorAccount};

export const useCreateDocumentMutation = ({
  onError,
  onSuccess,
}: UseMutationOptions<UseCreateDocumentMutationReturnType, Error, NewDocumentCreationDialogAction>) => {
  const createDocumentForNewClient = useCreateDocumentForNewClientMutationFn();
  const createDocumentForRecentClient = useCreateDocumentForRecentClientMutationFn();
  const createDocumentForExistingClient = useCreateDocumentForExistingClientMutationFn();

  return useMutation({
    mutationFn: async (action: NewDocumentCreationDialogAction) => {
      switch (action.type) {
        case 'new': {
          const {advisorCode} = action;
          return createDocumentForNewClient({advisorCode});
        }

        case 'recent': {
          const {latestTransaction, advisorCode, contractHolder} = action;

          return createDocumentForRecentClient({
            latestTransaction,
            advisorCode,
            contractHolder,
          });
        }

        case 'newAccount': {
          const {advisorCode, investor} = action;
          return createDocumentForExistingClient({advisorCode, investor});
        }

        case 'existingAccount': {
          const {advisorCode, investor, account} = action;
          return createDocumentForExistingClient({advisorCode, investor, account});
        }
      }
    },
    onError,
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
      analyticsHandler.webFormStarted({
        form_id: data.docId,
      });
    },
  });
};
